// useSiteList.js
import { useState, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { get, del, put } from "@utils/axios_api";
import { toast } from 'react-toastify';
import { AuthContext, useAdminCompany } from "@context";
import { ROLES } from "@constants/roles";

const fetchSiteList = async (params, isAdmin) => {
  const url = isAdmin
        ? "/admin/site/siteList"
        : "/user/site/siteList";
  const response = await get(url, { params });
  return response;
};

// (새 함수) 실제 삭제 API 호출
async function fetchDeleteSites(siteCodes, isAdmin) {
  // DELETE /api/admin/corp/deleteCorp?compCodes=AAA,BBB,CCC
  const paramString = siteCodes.join(",");
  const url = isAdmin
        ? `/admin/site/deleteSites?siteCodes=${paramString}`
        : `/user/site/deleteSites?siteCodes=${paramString}`;
  const response = await put(url);
  return response;
}

export function useSiteList(compCode ="") {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItems, setSelectedItems] = useState([]);
  const { auth } = useContext(AuthContext);
  const isAdmin = auth.role === ROLES.SUPER_ADMIN || auth.role === ROLES.ADMIN;

  const methods = useForm({
    defaultValues: {
    },
    shouldFocusError: false,
  });

  const { watch, setValue } = methods;

  const pageIndex = parseInt(searchParams.get("sitePageIndex") || "1", 10);
  const recordCountPerPage = parseInt(searchParams.get("sitePageSize") || "10", 10);
  const sortField = searchParams.get("sortField") || "";
  const sortOrder = searchParams.get("sortOrder") || "";

  const queryParam = {
    pageIndex,
    recordCountPerPage,
    compCode: compCode || "",
    sortField,
    sortOrder
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["selectSiteList", queryParam],
    queryFn: () => fetchSiteList(queryParam, isAdmin),
    keepPreviousData: true,
    staleTime: 600000,
    // USER는 compCode가 있어야 호출 (빈 compCode로 전체조회되는 것 방지)
    enabled: isAdmin || !!compCode,
  });

  const refreshList = () => {
    refetch();
  };

  // 검색 폼 submit
  const onSubmit = (values) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("sitePageIndex", "1");
    setSearchParams(newParams);
  };

  // 페이지 변경
  const handlePageChange = (targetPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("sitePageIndex", targetPage);
    setSearchParams(newParams);
  };

  const changePageSize = (size) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("sitePageSize", String(size));
    newParams.set("sitePageIndex", "1");
    setSearchParams(newParams);
  };

  // 전체 선택
  const handleSelectAll = (e) => {
    if (!data || !data.resultList) return;
    const { checked } = e.target;
    if (checked) {
      const allIds = data.resultList.map((item) => Number(item.siteCode));
      setSelectedItems(allIds);
    } else {
      setSelectedItems([]);
    }
  };

  // 단일 선택
  const handleSelectItem = (siteCode) => {
    const code = Number(siteCode);
    if (selectedItems.includes(code)) {
      setSelectedItems(selectedItems.filter((id) => id !== code));
    } else {
      setSelectedItems([...selectedItems, code]);
    }
  };

  // (수정) handleDelete: 이벤트 파라미터 없음, API 호출
  const handleDelete = async () => {

    try {
      const res = await fetchDeleteSites(selectedItems, isAdmin);
      if (res?.status === false) {
        toast.error(res.message || "삭제 실패");
      } else {
        toast.success(res?.message || "삭제 성공");
        refreshList();
        setSelectedItems([]);
      }
    } catch (err) {
      console.error("삭제 오류:", err);
    }
  };

  const isAllChecked =
    data?.resultList &&
    data.resultList.length > 0 &&
    selectedItems.length === data.resultList.length;

  return {
    methods,
    data,
    isLoading,
    isError,
    error,
    watch,
    setValue,
    selectedItems,
    isAllChecked,
    onSubmit,
    handlePageChange,
    changePageSize,
    handleSelectAll,
    handleSelectItem,
    handleDelete,
    pageIndex,
    recordCountPerPage,
    refetch,
    refreshList,
  };
}